// src/shared/utils/mobileViewportStability.js
// Mobile/PWA visualViewport 안정화 공용 유틸.
// - 키보드 입력 중에는 마지막 정상 viewport를 유지한다.
// - 키보드가 닫힌 직후 iOS PWA가 잠깐 내보내는 축소 viewport를 정상값으로 덮어쓰지 않는다.
// - 모든 입력 계열 freeze dataset을 한 곳에서 동일하게 판정한다.

const FREEZE_ATTRIBUTES = [
  'data-npc-mobile-input-viewport-freeze',
  'data-npc-table-viewport-freeze',
  'data-npc-dm-input-viewport-freeze',
  'data-npc-event-modal-viewport-freeze',
];

const FREEZE_DATASET_KEYS = [
  'npcMobileInputViewportFreeze',
  'npcTableViewportFreeze',
  'npcDmInputViewportFreeze',
  'npcEventModalViewportFreeze',
];

export function isMobileViewportFrozen() {
  if (typeof document === 'undefined') return false;
  const ds = document.documentElement?.dataset;
  if (!ds) return false;
  return FREEZE_DATASET_KEYS.some((key) => ds?.[key] === '1');
}

export function createMobileViewportStabilizer({
  recoveryMs = 1400,
  minKeyboardDropPx = 72,
  widthToleranceRatio = 0.06,
} = {}) {
  let stable = null;
  let sawFreeze = false;
  let recoveryUntil = 0;

  const accept = (next) => {
    stable = next;
    return next;
  };

  return {
    read(next) {
      if (!next || !Number.isFinite(Number(next.w)) || !Number.isFinite(Number(next.h))) {
        return stable || next;
      }

      const frozen = isMobileViewportFrozen();
      const now = Date.now();

      if (frozen) {
        sawFreeze = true;
        // 컴포넌트가 키보드가 열린 상태에서 처음 마운트된 예외만 현재 값을 seed로 사용한다.
        if (!stable) stable = next;
        return stable;
      }

      if (sawFreeze) {
        sawFreeze = false;
        recoveryUntil = now + recoveryMs;
      }

      if (!stable) return accept(next);

      if (now < recoveryUntil) {
        const stableW = Math.max(1, Number(stable.w) || 1);
        const stableH = Math.max(1, Number(stable.h) || 1);
        const nextW = Math.max(1, Number(next.w) || 1);
        const nextH = Math.max(1, Number(next.h) || 1);
        const widthTolerance = Math.max(16, stableW * widthToleranceRatio);
        const sameWidth = Math.abs(nextW - stableW) <= widthTolerance;
        const meaningfulDrop = Math.max(minKeyboardDropPx, stableH * 0.10);
        const recoveredEnough = nextH >= stableH - 24;

        // 방향 전환처럼 폭 자체가 크게 달라진 경우는 키보드 잔여 resize가 아니므로 즉시 수용한다.
        if (!sameWidth) {
          recoveryUntil = 0;
          return accept(next);
        }

        // 정상 높이로 돌아왔거나 더 커졌으면 즉시 복구 완료 처리한다.
        if (recoveredEnough) {
          recoveryUntil = 0;
          return accept(next);
        }

        // iOS/PWA는 키보드 dismiss 직후에도 수백 ms 동안 작은 visualViewport.height를
        // 한두 번 더 전달할 수 있다. 이 값으로 Root/Stage 높이를 덮어쓰지 않는다.
        if (stableH - nextH >= meaningfulDrop) {
          return stable;
        }
      }

      return accept(next);
    },

    reset(next = null) {
      stable = next;
      sawFreeze = false;
      recoveryUntil = 0;
    },

    getStable() {
      return stable;
    },
  };
}

export function bindMobileViewportSignals(sync) {
  if (typeof window === 'undefined' || typeof document === 'undefined' || typeof sync !== 'function') {
    return () => {};
  }

  const timers = new Set();
  const scheduleRecoveryBurst = () => {
    // 연속 focus/blur가 빠르게 반복되면 이전 burst timer를 먼저 제거해서
    // 오래된 키보드 세션의 재측정이 현재 viewport를 다시 건드리지 않게 한다.
    for (const timer of timers) clearTimeout(timer);
    timers.clear();

    // dataset freeze가 풀리는 순간 resize 이벤트가 더 이상 오지 않는 iOS PWA도 있어서
    // 몇 차례 재측정해 실제 정상 viewport 복구를 확실히 잡는다.
    for (const delay of [0, 80, 220, 450, 800, 1250, 1550]) {
      const timer = setTimeout(() => {
        timers.delete(timer);
        sync();
      }, delay);
      timers.add(timer);
    }
  };

  window.addEventListener('resize', sync);
  window.addEventListener('orientationchange', sync);
  window.addEventListener('pageshow', sync);

  const vv = window.visualViewport;
  if (vv) {
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
  }

  let observer = null;
  try {
    observer = new MutationObserver(() => scheduleRecoveryBurst());
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: FREEZE_ATTRIBUTES,
    });
  } catch (_) {}

  return () => {
    window.removeEventListener('resize', sync);
    window.removeEventListener('orientationchange', sync);
    window.removeEventListener('pageshow', sync);
    if (vv) {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
    }
    try { observer?.disconnect?.(); } catch (_) {}
    for (const timer of timers) clearTimeout(timer);
    timers.clear();
  };
}

let safeAreaProbe = null;

export function readCssSafeAreaInsets() {
  if (typeof window === 'undefined' || typeof document === 'undefined' || !document.body) {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }

  try {
    if (!safeAreaProbe || !safeAreaProbe.isConnected) {
      const probe = document.createElement('div');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText = [
        'position:fixed',
        'left:-9999px',
        'top:-9999px',
        'width:0',
        'height:0',
        'visibility:hidden',
        'pointer-events:none',
        'padding-top:env(safe-area-inset-top, 0px)',
        'padding-right:env(safe-area-inset-right, 0px)',
        'padding-bottom:env(safe-area-inset-bottom, 0px)',
        'padding-left:env(safe-area-inset-left, 0px)',
      ].join(';');
      document.body.appendChild(probe);
      safeAreaProbe = probe;
    }

    const cs = window.getComputedStyle(safeAreaProbe);
    const px = (value) => {
      const n = Number.parseFloat(String(value || '0'));
      return Number.isFinite(n) && n > 0 ? n : 0;
    };
    return {
      top: px(cs.paddingTop),
      right: px(cs.paddingRight),
      bottom: px(cs.paddingBottom),
      left: px(cs.paddingLeft),
    };
  } catch (_) {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
}
